/**
 * Force every `Date` locale-string call in the frontend to render in
 * Baghdad time (Asia/Baghdad, UTC+3, no DST) regardless of the device
 * timezone.
 *
 * WHY: The app is used by admins/customers in Iraq. Timestamps stored in
 * UTC would otherwise render in whatever timezone the device happens to
 * be set to (a phone with US time, a laptop travelling abroad, etc.),
 * which caused wrong "قطع الفواتير" times.
 *
 * HOW: Monkey-patch the three locale-string methods to inject
 * `timeZone: "Asia/Baghdad"` when the caller didn't specify one. Any
 * callsite that DOES specify a timezone keeps its choice — this is
 * additive, not destructive.
 *
 * Loaded once from `src/index.js` before React renders.
 */
const TZ = "Asia/Baghdad";
const LOCALE = "ar-IQ";

function patch(methodName) {
  const proto = Date.prototype;
  const original = proto[methodName];
  if (!original || original.__baghdadPatched) return;
  const wrapped = function (locales, options) {
    // Normalize signature — some callers pass options as the first arg.
    let loc = locales;
    let opts = options;
    if (locales && typeof locales === "object" && !Array.isArray(locales) && !(locales instanceof Intl.Locale)) {
      opts = locales;
      loc = undefined;
    }
    const nextOpts = opts && typeof opts === "object" ? { ...opts } : {};
    if (!nextOpts.timeZone) nextOpts.timeZone = TZ;
    return original.call(this, loc || LOCALE, nextOpts);
  };
  wrapped.__baghdadPatched = true;
  proto[methodName] = wrapped;
}

patch("toLocaleString");
patch("toLocaleDateString");
patch("toLocaleTimeString");


// ─── Baghdad-TZ date-string helpers ───────────────────────────────
// Every date-range filter in the app must compute "today" and offsets
// from a fixed Baghdad wall-clock — NOT the browser's UTC clock. That
// bug caused new driver bundles created between 00:00–03:00 Baghdad to
// disappear from the "today" list because the browser's UTC date was
// still the previous day. Use these helpers everywhere.
const BAGHDAD_OFFSET_HOURS = 3;

/**
 * Return today's date in Baghdad time as an ISO YYYY-MM-DD string —
 * safe to pass to `<input type="date">` or a backend query filter.
 */
export const baghdadToday = () => {
  const now = new Date();
  const baghdadMs = now.getTime() + BAGHDAD_OFFSET_HOURS * 3600 * 1000;
  return new Date(baghdadMs).toISOString().slice(0, 10);
};

/**
 * Return the Baghdad-local date `daysBack` days before today,
 * formatted YYYY-MM-DD.
 */
export const baghdadDaysAgo = (daysBack) => {
  const now = new Date();
  const baghdadMs = now.getTime() + BAGHDAD_OFFSET_HOURS * 3600 * 1000;
  return new Date(baghdadMs - daysBack * 86400 * 1000).toISOString().slice(0, 10);
};

/**
 * Return the first day of the current Baghdad month as YYYY-MM-DD.
 */
export const baghdadFirstOfMonth = () => {
  const t = baghdadToday();
  return t.slice(0, 7) + "-01";
};
